// Support system constants and configurations

import { SupportCategory, SupportPriority, SupportStatus } from '@/types/support';

// Status mappings for display
export const SUPPORT_STATUS_LABELS: Record<SupportStatus, string> = {
  new: 'New',
  open: 'Open',
  in_progress: 'In Progress',
  waiting_info: 'Waiting for Info',
  resolved: 'Resolved',
  closed: 'Closed',
  cancelled: 'Cancelled'
};

// Status colors for UI
export const SUPPORT_STATUS_COLORS: Record<SupportStatus, string> = {
  new: 'bg-purple-100 text-purple-800',
  open: 'bg-blue-100 text-blue-800',
  in_progress: 'bg-yellow-100 text-yellow-800',
  waiting_info: 'bg-amber-100 text-amber-800',
  resolved: 'bg-green-100 text-green-800',
  closed: 'bg-gray-100 text-gray-800',
  cancelled: 'bg-red-100 text-red-800'
};

// Priority mappings for display
export const SUPPORT_PRIORITY_LABELS: Record<SupportPriority, string> = {
  urgent: 'Urgent',
  high: 'High',
  medium: 'Medium',
  low: 'Low'
};

// Priority colors for UI
export const SUPPORT_PRIORITY_COLORS: Record<SupportPriority, string> = {
  urgent: 'bg-red-100 text-red-800',
  high: 'bg-amber-100 text-amber-800',
  medium: 'bg-yellow-100 text-yellow-800',
  low: 'bg-blue-100 text-blue-800'
};

// Category mappings for display
export const SUPPORT_CATEGORY_LABELS: Record<SupportCategory, string> = {
  student_management: 'Student Management',
  applications: 'Applications',
  capacity: 'Capacity',
  transportation: 'Transportation',
  reports: 'Reports',
  notifications: 'Notifications',
  portal_sync: 'Portal Sync',
  user_access: 'User Access',
  other: 'Other'
};

// Category icons (using lucide-react icon names)
export const SUPPORT_CATEGORY_ICONS: Record<SupportCategory, string> = {
  student_management: 'Users',
  applications: 'FileText',
  capacity: 'Building',
  transportation: 'Bus',
  reports: 'BarChart',
  notifications: 'Bell',
  portal_sync: 'RefreshCw',
  user_access: 'Key',
  other: 'HelpCircle'
};

// API endpoints
export const SUPPORT_API_ENDPOINTS = {
  requests: '/support/requests',
  templates: '/support/templates',
  comments: '/support/comments'
} as const;

// Pagination defaults
export const SUPPORT_PAGINATION = {
  defaultPageSize: 20,
  pageSizeOptions: [10, 20, 50, 100]
} as const;

// Cache keys for React Query
export const SUPPORT_QUERY_KEYS = {
  all: ['support'] as const,
  requests: (schoolId: string) => ['support', 'requests', schoolId] as const,
  request: (id: string) => ['support', 'request', id] as const,
  templates: (category?: SupportCategory) => ['support', 'templates', category] as const,
  comments: (requestId: string) => ['support', 'comments', requestId] as const
} as const;

// Retry configuration
export const SUPPORT_RETRY_CONFIG = {
  maxRetries: 3,
  retryDelay: (attemptIndex: number) => Math.min(1000 * 2 ** attemptIndex, 30000),
  retryCondition: (error: any) => {
    // Retry on network errors or 5xx status codes
    return !error.response || (error.response.status >= 500 && error.response.status < 600);
  }
} as const;

// Error messages
export const SUPPORT_ERROR_MESSAGES = {
  createRequest: 'Failed to create support request. Please try again.',
  updateRequest: 'Failed to update support request. Please try again.',
  loadRequests: 'Failed to load support requests. Please refresh the page.',
  loadRequest: 'Failed to load support request details.',
  addComment: 'Failed to add comment. Please try again.',
  loadTemplates: 'Failed to load support templates.',
  networkError: 'Network error. Please check your connection.',
  unauthorized: 'You are not authorized to perform this action.',
  notFound: 'Support request not found.'
} as const;

// Success messages
export const SUPPORT_SUCCESS_MESSAGES = {
  createRequest: 'Support request created successfully!',
  updateRequest: 'Support request updated successfully!',
  addComment: 'Comment added successfully!',
  resolveRequest: 'Support request marked as resolved!',
  closeRequest: 'Support request closed successfully!'
} as const;

// File upload configuration
export const SUPPORT_FILE_CONFIG = {
  maxFileSize: 5 * 1024 * 1024, // 5MB
  acceptedFileTypes: ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'application/pdf'],
  maxFiles: 5
} as const;

// Real-time subscription events
export const SUPPORT_SUBSCRIPTION_EVENTS = {
  requestCreated: 'support:request:created',
  requestUpdated: 'support:request:updated',
  commentAdded: 'support:comment:added',
  statusChanged: 'support:status:changed'
} as const;